import axios, { AxiosError, AxiosRequestConfig, AxiosTransformer, CancelToken } from 'axios';
import { ROUTES } from 'routes';
import history from 'utils/history';
import { getSessionToken, removeSessionToken } from 'utils/session';

import { version } from '../../package.json';
import { SET_AUTH_STATUS } from './actions';
import { store } from './store';

// const BASE_URL = '';
// const BASE_URL = 'https://princeton-lti-mock.acadly.com';
// const BASE_URL = 'https://acadly-lti-mock.acadly.com';
const BASE_URL = process.env.REACT_APP_BASE_URL;

export const API = {
  // Dummy APIs
  GET_ALL_FACULTY_ACCOUNTS: '/users_get_faculty_all',
  CACHE_FACULTY: '/users_cache_faculty',
  BUILD_IMPORT_STUDENTS_CACHE: '/build_import_students_cache',

  // New APIs
  LTI_LAUNCH_REDIR: (token: string) => `/lti/launch_redirect?access_token=${token}`,
  LTI_SET_PW: '/lti/set/password',
  LTI_SESSION: '/lti/session',
  LTI_VERIFY_GET_CODE: '/lti/verify/getcode',
  LTI_VERIFY_TEMP_CODE: '/lti/verify/tempcode',
  LTI_FORGOT_PW: '/lti/forgot-password',
  LTI_LINK_EMAIL: '/lti/link/email',
  LTI_LINK_PW: '/lti/link/password',
  LTI_LINK_VERIFY_NEW_EMAIL: '/lti/link/verify-new-email',
  LTI_GET_ACCESS: '/lti/get_access',
  LTI_VALIDATE_ACCESS_TOKEN: '/lti/session/validate',
  LTI_LOGOUT: '/lti/logout',
  LTI_IMPORT_MY_DATA: '/lti/import-my-data',
  COURSE_REQUEST_APPROVAL: '/api/courses/request_approval',
  COURSE_APPROVE_REQUEST: '/api/courses/approve_course',
  GET_COURSE_DETAILS: (id: string) => `/api/courses/raw/${id}`,
  COURSE_CREATE_SAVE_INFO: '/api/courses/create/save-info',
  COURSE_CREATE_SAVE_SCHEDULE: '/api/courses/create/save-schedule',
  COURSE_CREATE_SAVE_TIMELINE: '/api/courses/create/save-timeline',
  COURSE_CREATE_IMPORT_STUDENTS: '/api/courses/create/import-students',
  COURSE_CREATE_IMPORT_STUDENTS_STATUS: (id: string) => `/api/courses/create/status/student-import/${id}`,
  COURSE_CREATE_COMPLETE: '/api/courses/create',
  SIGNED_UP_FACULTIES: '/api/users/faculty/signedup',
  SIGNED_UP_STUDENTS: '/api/users/students/signedup',
  COURSES_ALL: '/api/courses/all',
  GET_TERMS: '/api/terms/get',
  SET_DEFAULT_TERM: '/api/terms/default/set',
  COURSES_IMPORTED: '/api/courses/imported',
  COURSE_DELETE: '/api/courses/delete',
  COURSE_GET_STATUS: (courseId: string) => `/api/courses/status/${courseId}`,
  COURSE_ENROLMENTS: (courseId: string) => `/api/courses/enrolments/${courseId}`,
  COURSE_TEAM_MEMBERS: (courseId: string) => `/api/courses/members/${courseId}`,
  COURSE_STUDENTS_SYNC: '/api/courses/sync/students',
  COURSE_STUDENTS_SYNC_STATUS: '/api/courses/sync/status/students',
  COURSE_MEMBERS_SYNC: '/api/courses/sync/members',
  ATTENDANCE_COURSE_ALL: '/api/courses/attendance',
  ATTENDANCE_CLASS: '/api/classes/attendance',
  ATTENDANCE_COURSE_ALL_STUDENTS: '/api/students/attendance',
  ATTENDANCE_STUDENT: '/api/students/attendance/individual',
  ATTENDANCE_ADMIN_STUDENT: '/api/students/attendance/individual/aggregate',
  ATTENDANCE_ADMIN_ALL_STUDENTS: '/api/students/attendance/all',
  ATTENDANCE_ADMIN_ALL_COURSES: '/api/courses/attendance/all',
  ATTENDANCE_EXPORT: '/api/courses/attendance/export',
  COURSE_ACTIVITY_SCORES: '/api/courses/activities',
  COURSE_ACTIVITY_DETAILS: '/api/courses/activities/details',
  COURSE_EXPORT_ACTIVITY_DETAILS: '/api/courses/activities/export',
  REMOVE_EXPORTED_ACTIVITY: `/api/courses/activities/export/remove`,
  REMOVE_EXPORTED_ATTENDANCE: `/api/courses/attendance/export/remove`,
  COURSE_ANALYTICS: '/api/courses/analytics/aggregate',
  STUDENT_AGGREGATE_ANALYTICS: '/api/students/analytics/aggregate',
  STUDNET_ANALYTICS: '/api/students/analytics',
  EXPORT_ALL_ATTENDANCE: '/api/courses/attendance/export/all',
};

/**
 * Makes a GET Call
 * @param {string} path
 * @param {object} data
 * @returns {AxiosPromise}
 * @constructor
 */
export const Fetch = (path: string, data: any = {}) => {
  axios.interceptors.request.use((config) => {
    const token = getSessionToken();
    config.headers.Authorization = 'Bearer ' + token;
    return config;
  });

  // TODO: Change base url.
  return axios({
    method: data.method || 'get',
    url: BASE_URL + path,
    data: data.data,
  });
};

export const mockRequest = async <T = any>(json: T, delay = 1000) => {
  return await new Promise<T>((resolve) => {
    setTimeout(() => resolve(json), delay);
  });
};

export type RequestCancelToken = CancelToken;
export const getCancelToken = () => axios.CancelToken.source();

const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = 'Bearer ' + token;
  } else {
    console.warn('session token not found!');
  }
  config.headers.AppVersion = version;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!(error as AxiosError).response) return Promise.reject(error);

    const e = error as AxiosError;
    const allowedURLs = [API.LTI_SESSION, API.LTI_SET_PW];

    if (
      !allowedURLs.includes(e.response?.config.url || '') &&
      (e.response?.status === 401 || e.response?.status === 403)
    ) {
      removeSessionToken();

      store.dispatch({
        type: SET_AUTH_STATUS,
        payload: false,
      });

      history.push({
        pathname: ROUTES.MESSAGE_PAGE,
        state: {
          message:
            'You have been logged out of the Acadly LTI plugin ' +
            'because you may have logged in from another instance',
        },
      });
    }

    return Promise.reject(error);
  }
);

export const request = async <T = any>(config: AxiosRequestConfig): Promise<T> => {
  const response = await api.request<T>({
    ...config,
    transformResponse: ([] as AxiosTransformer[]).concat(
      api.defaults.transformResponse || [],
      config.transformResponse || []
    ),
  });
  return response.data;
};
